import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  useSaveDailyOverride,
  useDeleteDailyOverride,
  getGetDailyOverrideQueryKey,
  useListCourses,
  useListClasses,
  ApiError,
  type Course,
  type Class,
  type DailyOverrideError,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";

const LIST_DAILY_OVERRIDES_PATH = "/api/diary/overrides";
import {
  ArrowDown,
  ArrowUp,
  Coffee,
  Loader2,
  Plus,
  Trash2,
  Pencil,
  RotateCcw,
} from "lucide-react";

interface ParsedSaveError {
  title: string;
  description: string;
  invalidCourseIds: number[];
  invalidClassIds: number[];
}

/**
 * Decoder for the typed `DailyOverrideError` returned by
 * PUT /api/diary/{date}/override. The orval-generated client throws an
 * `ApiError` whose `data` is the parsed JSON body; we read its fields
 * directly so we can show a specific toast per error code instead of the
 * generic message.
 *
 * For `invalid_references`, we also pull `details.courseIds` /
 * `details.classIds` so the drawer can flag the offending rows inline
 * instead of leaving the teacher to guess which period is bad.
 */
function mapSaveError(err: unknown): ParsedSaveError {
  let code: string | undefined;
  let serverMsg: string | undefined;
  let invalidCourseIds: number[] = [];
  let invalidClassIds: number[] = [];
  if (err instanceof ApiError) {
    const data = err.data as Partial<DailyOverrideError> | null;
    if (data && typeof data === "object") {
      if (typeof data.errorCode === "string") code = data.errorCode;
      if (typeof data.error === "string") serverMsg = data.error;
      const details = data.details;
      if (details && typeof details === "object") {
        const courseIds = (details as Record<string, unknown>).courseIds;
        if (Array.isArray(courseIds)) {
          invalidCourseIds = courseIds.filter((v): v is number => typeof v === "number");
        }
        const classIds = (details as Record<string, unknown>).classIds;
        if (Array.isArray(classIds)) {
          invalidClassIds = classIds.filter((v): v is number => typeof v === "number");
        }
      }
    }
  }
  switch (code) {
    case "invalid_date":
      return { title: "Could not save", description: "The date is not valid. Reload and try again.", invalidCourseIds, invalidClassIds };
    case "invalid_references":
      return {
        title: "Subject or class not found",
        description:
          invalidCourseIds.length + invalidClassIds.length > 0
            ? "We've highlighted the periods that need a different subject or class. Update them and save again."
            : serverMsg ?? "One of the periods uses a subject or class you no longer have access to. Pick a different one and try again.",
        invalidCourseIds,
        invalidClassIds,
      };
    case "invalid_slots":
      return { title: "Could not save", description: "Some periods are missing required information. Check each row and try again.", invalidCourseIds, invalidClassIds };
    default:
      return { title: "Could not save", description: serverMsg || "Try again.", invalidCourseIds, invalidClassIds };
  }
}

export interface DraftSlot {
  position: number;
  slotType: "period" | "break";
  label: string;
  courseId: number | null;
  classId: number | null;
  /**
   * Task #103: set when the courseId on this slot points at a course that no
   * longer exists (legacy data or a hard-delete via the permanent-delete
   * modal). Surfaced from the server's `courseMissing` flag on the override
   * response. Cleared once the user picks a different course (or clears it)
   * because the new value is, by definition, valid.
   */
  courseMissing?: boolean;
  /**
   * Task #142: same shape of fix as `courseMissing` for class references.
   * True when this slot's classId points at a hard-deleted class. Cleared
   * once the user picks a different class (or clears it).
   */
  classMissing?: boolean;
}

interface DailyOverrideDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: Date;
  initialSlots: DraftSlot[];
  hasOverride: boolean;
}

const NONE_VALUE = "__none";

export function DailyOverrideDrawer({
  open,
  onOpenChange,
  date,
  initialSlots,
  hasOverride,
}: DailyOverrideDrawerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const dateStr = format(date, "yyyy-MM-dd");

  const [slots, setSlots] = useState<DraftSlot[]>(initialSlots);
  // IDs the server flagged as not-owned on the most recent save attempt.
  // We keep the full set rather than only the still-referenced ones so a row
  // that switches and then switches back to the bad value re-flags itself.
  const [invalidRefs, setInvalidRefs] = useState<{
    courseIds: Set<number>;
    classIds: Set<number>;
  }>({ courseIds: new Set(), classIds: new Set() });

  // Seed the drawer's local state ONCE per open session — when `open`
  // transitions false → true. Critically, `initialSlots` must NOT be in the
  // dep array: its reference is recomputed on every parent render (React Query
  // background refetches, archived-class memos, etc.), so listing it here
  // would reset in-progress edits every time any query refetches while the
  // drawer is open. When `open` fires (closed → open), the closure already
  // captures the current `initialSlots` from that render, so initialisation
  // is always seeded with up-to-date data.
  const hasSeededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasSeededRef.current = false;   // arm for the next open
      return;
    }
    if (!hasSeededRef.current) {
      hasSeededRef.current = true;
      setSlots(initialSlots);
      setInvalidRefs({ courseIds: new Set(), classIds: new Set() });
    }
  // `initialSlots` intentionally omitted: see comment above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { data: courses = [] as Course[] } = useListCourses();
  const { data: classes = [] as Class[] } = useListClasses();

  const saveOverride = useSaveDailyOverride();
  const deleteOverride = useDeleteDailyOverride();

  const invalidateOverride = () => {
    queryClient.invalidateQueries({ queryKey: getGetDailyOverrideQueryKey(dateStr) });
    // Also invalidate every range-list of overrides (the week view consumer)
    // — we don't know which week range is currently mounted.
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) && q.queryKey[0] === LIST_DAILY_OVERRIDES_PATH,
    });
  };

  const moveSlot = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= slots.length) return;
    setSlots((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((s, i) => ({ ...s, position: i + 1 }));
    });
  };

  const addSlot = (slotType: "period" | "break") => {
    setSlots((prev) => {
      const nextPos = prev.length + 1;
      const fallback = slotType === "break" ? "Break" : `Period ${prev.filter((p) => p.slotType === "period").length + 1}`;
      return [
        ...prev,
        {
          position: nextPos,
          slotType,
          label: fallback,
          courseId: null,
          classId: null,
        },
      ];
    });
  };

  const removeSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, position: i + 1 })));
  };

  const updateSlot = (index: number, patch: Partial<DraftSlot>) => {
    setSlots((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        const next = { ...s, ...patch };
        // Task #103: any explicit change to courseId means the user has
        // resolved the broken reference (either by picking a real course or by
        // clearing it to "No course"). Drop the missing flag so the picker
        // stops showing the "(deleted course)" tag.
        if ("courseId" in patch) next.courseMissing = false;
        // Task #142: same logic for the class side — picking a new class
        // (or clearing it) resolves the dangling reference.
        if ("classId" in patch) next.classMissing = false;
        return next;
      }),
    );
  };

  // Task #103: one-tap "clear all broken references" — sets courseId to null
  // on every slot the server flagged as pointing at a deleted course. The
  // user still has to hit Save for the change to persist; until then the
  // banner just disappears from the UI.
  const clearAllMissingCourses = () => {
    setSlots((prev) =>
      prev.map((s) =>
        s.courseMissing ? { ...s, courseId: null, courseMissing: false } : s,
      ),
    );
  };

  const missingCourseCount = slots.filter((s) => s.courseMissing).length;

  const isPending = saveOverride.isPending || deleteOverride.isPending;

  // Save stays disabled while any slot still references a course/class the
  // server told us is bad — the next PUT would just fail again.
  const hasUnresolvedInvalidRefs = useMemo(
    () =>
      slots.some(
        (s) =>
          (s.courseId != null && invalidRefs.courseIds.has(s.courseId)) ||
          (s.classId != null && invalidRefs.classIds.has(s.classId)),
      ),
    [slots, invalidRefs],
  );

  const handleSave = async () => {
    const cleanedSlots = slots
      .map((s, i) => ({
        position: i + 1,
        slotType: s.slotType,
        label: s.label.trim() || (s.slotType === "break" ? "Break" : `Period ${i + 1}`),
        courseId: s.slotType === "period" ? s.courseId ?? null : null,
        classId: s.slotType === "period" ? s.classId ?? null : null,
      }));

    try {
      await saveOverride.mutateAsync({ date: dateStr, data: { slots: cleanedSlots } });
      // Await both refetches before closing so the override transition finds
      // server-fresh diary entries: the entryMap fallback needs slot-{id} entries
      // in cache when the new override-{n} PeriodCards mount.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: getGetDailyOverrideQueryKey(dateStr) }),
        queryClient.refetchQueries({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === "/api/diary" &&
            !!q.queryKey[1] &&
            typeof q.queryKey[1] === "object",
        }),
      ]);
      // Background-invalidate the weekly overrides list for the week-view badge.
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === LIST_DAILY_OVERRIDES_PATH,
      });
      setInvalidRefs({ courseIds: new Set(), classIds: new Set() });
      toast({ title: "Today's schedule saved", description: "Your custom periods are now active for this day." });
      onOpenChange(false);
    } catch (err) {
      // The API now returns typed error codes (invalid_date, invalid_slots,
      // invalid_references). Surface a specific message for each so support
      // staff know what to fix instead of seeing a generic save failure.
      const { title, description, invalidCourseIds, invalidClassIds } = mapSaveError(err);
      if (invalidCourseIds.length > 0 || invalidClassIds.length > 0) {
        setInvalidRefs((prev) => ({
          courseIds: new Set([...prev.courseIds, ...invalidCourseIds]),
          classIds: new Set([...prev.classIds, ...invalidClassIds]),
        }));
      }
      toast({ title, description, variant: "destructive" });
    }
  };

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const handleReset = async () => {
    try {
      await deleteOverride.mutateAsync({ date: dateStr });
      // Await both refetches in parallel before closing the drawer.
      //
      // Why this matters: `invalidateOverride()` starts async background
      // refetches. If the drawer closes before they finish, the diary page
      // re-renders with whatever happens to be in cache — which can be stale
      // or empty (e.g. if the DiaryContext briefly resets activeTimetableId
      // after the timetables list is invalidated by onOpenChange, disabling the
      // diary-entries query). Awaiting here guarantees:
      //   1. The override query returns null  → hasOverride = false on next render
      //   2. Diary entries are server-fresh   → entryMap finds slot-{id} notes
      await Promise.all([
        queryClient.refetchQueries({ queryKey: getGetDailyOverrideQueryKey(dateStr) }),
        queryClient.refetchQueries({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === "/api/diary" &&
            !!q.queryKey[1] &&
            typeof q.queryKey[1] === "object",
        }),
      ]);
      // Background-invalidate the weekly overrides list for the week-view badge.
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === LIST_DAILY_OVERRIDES_PATH,
      });
      toast({ title: "Reset to timetable", description: "This day now follows your base timetable again." });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Could not reset",
        description: getApiErrorMessage(err, "Try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 gap-0">
        <SheetHeader className="p-6 pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-primary" />
            Customise today
          </SheetTitle>
          <SheetDescription>
            Edit periods just for {format(date, "EEE d MMM yyyy")}. Your base timetable stays unchanged.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {/* Task #103: one-time, in-drawer banner offering to clear every
              reference to a course that no longer exists. Disappears the
              moment all broken refs are cleared (either via this button or
              by editing the rows by hand). */}
          {missingCourseCount > 0 && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm flex items-start gap-2"
              data-testid="override-deleted-course-banner"
            >
              <div className="flex-1 text-amber-900">
                <div className="font-semibold">
                  {missingCourseCount === 1
                    ? "1 period points at a deleted subject."
                    : `${missingCourseCount} periods point at a deleted subject.`}
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  These were saved before the subject was removed. Clear them so
                  the next save doesn't re-stamp the broken reference.
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllMissingCourses}
                className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100 flex-shrink-0"
                data-testid="override-deleted-course-clear-all"
              >
                Clear them
              </Button>
            </div>
          )}

          {slots.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-12 border-2 border-dashed border-gray-200 rounded-xl">
              No periods yet. Add one below to get started.
            </div>
          ) : (
            slots.map((slot, index) => {
              const courseInvalid =
                slot.courseId != null && invalidRefs.courseIds.has(slot.courseId);
              const classInvalid =
                slot.classId != null && invalidRefs.classIds.has(slot.classId);
              return (
                <SlotRow
                  key={index}
                  index={index}
                  slot={slot}
                  isFirst={index === 0}
                  isLast={index === slots.length - 1}
                  courses={courses}
                  classes={classes}
                  courseInvalid={courseInvalid}
                  classInvalid={classInvalid}
                  onMoveUp={() => moveSlot(index, -1)}
                  onMoveDown={() => moveSlot(index, 1)}
                  onChange={(patch) => updateSlot(index, patch)}
                  onRemove={() => removeSlot(index)}
                />
              );
            })
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => addSlot("period")}>
              <Plus className="h-3.5 w-3.5" /> Add period
            </Button>
            <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => addSlot("break")}>
              <Coffee className="h-3.5 w-3.5" /> Add break
            </Button>
          </div>
        </div>

        <div className="border-t p-4 flex items-center gap-2 bg-gray-50">
          {hasOverride && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setResetConfirmOpen(true)}
              disabled={isPending}
              className="gap-1.5 text-gray-500 hover:text-gray-800"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to timetable
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isPending || hasUnresolvedInvalidRefs}
              data-write-action
              title={
                hasUnresolvedInvalidRefs
                  ? "Fix the highlighted period(s) before saving."
                  : undefined
              }
            >
              {isPending && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
              Save changes
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset to original timetable?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                This will remove today's customised periods and return the day
                to the original timetable.
              </p>
              <p>
                Notes linked to the original timetabled periods will remain
                visible.
              </p>
              <p>
                Notes or records added to custom-only periods may no longer
                appear in this day view after reset.
              </p>
              <p className="font-medium text-foreground">
                If you need to keep those notes visible, cancel and copy them
                into the relevant timetabled period before resetting.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleReset}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Reset to timetable
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

interface SlotRowProps {
  index: number;
  slot: DraftSlot;
  isFirst: boolean;
  isLast: boolean;
  courses: Course[];
  classes: Class[];
  courseInvalid: boolean;
  classInvalid: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onChange: (patch: Partial<DraftSlot>) => void;
  onRemove: () => void;
}

function SlotRow({
  index, slot, isFirst, isLast,
  courses, classes,
  courseInvalid, classInvalid,
  onMoveUp, onMoveDown, onChange, onRemove,
}: SlotRowProps) {
  const isBreak = slot.slotType === "break";
  const hasInvalidRef = !isBreak && (courseInvalid || classInvalid);

  // Filter classes by selected course (fall back to the full list when the
  // selected course has no associated classes, so users can still pick one).
  const classOptions = useMemo(() => {
    if (!slot.courseId) return classes;
    const filtered = classes.filter((c) => c.courseIds?.includes(slot.courseId!));
    return filtered.length > 0 ? filtered : classes;
  }, [classes, slot.courseId]);

  const containerClass = hasInvalidRef
    ? "border-red-400 bg-red-50"
    : isBreak
      ? "border-dashed border-gray-300 bg-gray-50"
      : "border-gray-200 bg-white";

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${containerClass}`}>
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
          {isBreak ? "Break" : `#${index + 1}`}
        </Badge>
        <Input
          value={slot.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder={isBreak ? "Break label" : "e.g. Period 1"}
          className="h-8 text-sm flex-1"
        />
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveUp} disabled={isFirst} title="Move up">
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveDown} disabled={isLast} title="Move down">
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={onRemove} title="Remove">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {!isBreak && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Select
              // When courseMissing, leave the Select uncontrolled (value="")
              // so the placeholder — which we set to "(deleted course)" — is
              // what renders inside the trigger. If we passed NONE_VALUE here,
              // the trigger would show the matching "No course" item label
              // instead, hiding the warning.
              value={
                slot.courseMissing
                  ? ""
                  : slot.courseId
                    ? String(slot.courseId)
                    : NONE_VALUE
              }
              onValueChange={(v) =>
                onChange({
                  courseId: v === NONE_VALUE ? null : Number(v),
                  classId: null,
                })
              }
            >
              <SelectTrigger
                className={`h-8 text-xs ${courseInvalid || slot.courseMissing ? "border-red-400 ring-1 ring-red-200 text-red-600" : ""}`}
              >
                <SelectValue placeholder={slot.courseMissing ? "(deleted subject)" : "No subject"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>No subject</SelectItem>
                {courses.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {slot.courseMissing && (
              // Task #103: row-level marker so the teacher can see *which*
              // period the banner is talking about. Disappears as soon as the
              // user picks a new course (or hits "Clear them" in the banner).
              <span
                className="inline-block text-[10px] font-medium text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                data-testid="override-row-deleted-course"
              >
                (deleted subject)
              </span>
            )}
          </div>

          <div className="space-y-1">
            <Select
              // Task #142: mirrors the courseMissing handling above —
              // when classMissing, leave uncontrolled so the
              // "(deleted class)" placeholder shows in the trigger.
              value={
                slot.classMissing
                  ? ""
                  : slot.classId
                    ? String(slot.classId)
                    : NONE_VALUE
              }
              onValueChange={(v) => onChange({ classId: v === NONE_VALUE ? null : Number(v) })}
            >
              <SelectTrigger
                className={`h-8 text-xs ${classInvalid || slot.classMissing ? "border-red-400 ring-1 ring-red-200 text-red-600" : ""}`}
              >
                <SelectValue placeholder={slot.classMissing ? "(deleted class)" : "No class"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>No class</SelectItem>
                {classOptions.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {slot.classMissing && (
              <span
                className="inline-block text-[10px] font-medium text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                data-testid="override-row-deleted-class"
              >
                (deleted class)
              </span>
            )}
          </div>
        </div>
      )}
      {hasInvalidRef && (
        <p className="text-xs text-red-600 leading-snug">
          {courseInvalid && classInvalid
            ? "This period's subject and class are no longer available. Pick replacements or remove the period."
            : courseInvalid
              ? "This period's subject is no longer available. Pick a different subject or remove the period."
              : "This period's class is no longer available. Pick a different class or remove the period."}
        </p>
      )}
    </div>
  );
}
